import { Crop, CropType } from '../../constants/CropConstants';

export interface MonthDay {
  month: number;
  day: number;
}

export interface SeedRateForCrop {
  start: MonthDay;
  end: MonthDay;
  /** x is the inclusive day number in the range: start is 1, the next day is 2, etc. */
  formula: (x: number) => number;
}

export interface SeedRateResult {
  seedRate: number;
  isOutsideRange: boolean;
}

interface DayNumberWithinRangeResult {
  dayNumber: number;
  isOutsideRange: boolean;
}

const DAYS_IN_YEAR = 365;
const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * One entry per crop: edit the date range and formula here.
 *
 * Example for a range from 1 August through 1 September:
 *   start: { month: 8, day: 1 },
 *   end: { month: 9, day: 1 },
 *   formula: (x) => 200 + 2 * x,
 *
 * In that example x is 1 on 1 August and 32 on 1 September.
 */
export const cropData: Partial<Record<Crop, SeedRateForCrop>> = {
  [CropType.WHEAT_WINTER]: {
    start: { month: 9, day: 1 },
    end: { month: 10, day: 15 },
    formula: (x) => 5.3 * x + 195,
  },
  [CropType.BARLEY_WINTER_TWO_ROW]: {
    start: { month: 9, day: 1 },
    end: { month: 10, day: 1 },
    formula: (x) => 3 * x + 207,
  },
  [CropType.BARLEY_WINTER_SIX_ROW]: {
    start: { month: 9, day: 1 },
    end: { month: 10, day: 1 },
    formula: (x) => 3 * x + 177,
  },
  [CropType.RYE_WINTER]: {
    start: { month: 9, day: 1 },
    end: { month: 10, day: 15 },
    formula: (x) => 4.5 * x + 175,
  },
  [CropType.RYE_HYBRID_WINTER]: {
    start: { month: 9, day: 1 },
    end: { month: 10, day: 15 },
    formula: (x) => 0.0286 * x + 1.2714,
  },
  [CropType.TRITICALE_WINTER]: {
    start: { month: 9, day: 1 },
    end: { month: 10, day: 15 },
    formula: (x) => 3.7 * x + 154,
  },
  [CropType.RAPESEED_WINTER]: {
    start: { month: 8, day: 1 },
    end: { month: 8, day: 31 },
    formula: (x) => 0.017 * x + 0.375,
  },
  [CropType.WINTER_BARLEY_HYBRID]: {
    start: { month: 9, day: 1 },
    end: { month: 9, day: 25 },
    formula: (x) => 0.02 * x + 1.28,
  },
};

/** Returns the configured crop formula evaluated for the supplied date. */
export function getSeedRate(crop: string, date: Date): SeedRateResult {
  const config = cropData[crop as Crop];

  if (!config) {
    throw new Error(`No seed-rate formula configured for crop: ${crop}`);
  }

  const { dayNumber, isOutsideRange } = getDayNumberWithinRangeResult(
    config.start,
    config.end,
    date
  );

  return {
    seedRate: config.formula(dayNumber),
    isOutsideRange,
  };
}

/**
 * Converts a date to its inclusive position in an annually recurring range.
 * Ranges may cross New Year (for example, 1 November through 1 March).
 */
export function getDayNumberWithinRange(start: MonthDay, end: MonthDay, date: Date): number {
  return getDayNumberWithinRangeResult(start, end, date).dayNumber;
}

function getDayNumberWithinRangeResult(
  start: MonthDay,
  end: MonthDay,
  date: Date
): DayNumberWithinRangeResult {
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date');
  }

  validateMonthDay(start);
  validateMonthDay(end);

  const startDay = getDayOfYear(start);
  const endDay = getDayOfYear(end);
  const dateDay = getDayOfYear({ month: date.getMonth() + 1, day: date.getDate() });

  if (startDay <= endDay) {
    if (dateDay < startDay) {
      return { dayNumber: 1, isOutsideRange: true };
    }

    if (dateDay > endDay) {
      return { dayNumber: endDay - startDay + 1, isOutsideRange: true };
    }

    return { dayNumber: dateDay - startDay + 1, isOutsideRange: false };
  }

  if (dateDay >= startDay) {
    return { dayNumber: dateDay - startDay + 1, isOutsideRange: false };
  }

  if (dateDay <= endDay) {
    return {
      dayNumber: DAYS_IN_YEAR - startDay + dateDay + 1,
      isOutsideRange: false,
    };
  }

  const rangeLength = DAYS_IN_YEAR - startDay + endDay + 1;
  const daysAfterEnd = dateDay - endDay;
  const daysBeforeStart = startDay - dateDay;

  return daysAfterEnd <= daysBeforeStart
    ? { dayNumber: rangeLength, isOutsideRange: true }
    : { dayNumber: 1, isOutsideRange: true };
}

function validateMonthDay(monthDay: MonthDay): void {
  const daysInMonth = daysPerMonth[monthDay.month - 1];
  if (!daysInMonth || monthDay.day < 1 || monthDay.day > daysInMonth) {
    throw new Error(`Invalid month/day: ${monthDay.month}/${monthDay.day}`);
  }
}

function getDayOfYear(monthDay: MonthDay): number {
  return daysPerMonth.slice(0, monthDay.month - 1).reduce((a, b) => a + b, 0) + monthDay.day;
}
